/* eslint-disable @typescript-eslint/no-explicit-any -- Dynamic protobuf-to-view conversion is confined to this tested boundary. */
// The UI keeps its existing view models; protobuf types stay at the RPC boundary.
import { toJson, type JsonObject } from "@bufbuild/protobuf";
import { ValueSchema, timestampDate } from "@bufbuild/protobuf/wkt";
import * as app from "./gen/app_pb";
import * as cfg from "./gen/configurable_pb";
import * as strategy from "./gen/strategy_pb";
import type { BacktestResult as RpcResult } from "./gen/backtest_pb";
import type * as ui from "./types";
import type { StrategyInfoResponse } from "@/lib/strategy-info";

// Plain nested output records use snake_case in the UI. Config/metadata and map
// keys are opaque user data, and must never have their spelling rewritten.
export function plain(value: unknown): any {
  if (typeof value === "bigint") {
    const n = Number(value);
    if (!Number.isSafeInteger(n)) throw new RangeError("RPC integer exceeds the UI numeric range");
    return n;
  }
  if (Array.isArray(value)) return value.map(plain);
  if (!value || typeof value !== "object") return value;
  const m = value as Record<string, any>;
  if (m.$typeName === "google.protobuf.Timestamp") return timestampDate(m as any).toISOString();
  if (/^cryptotrader\..*Id$/.test(m.$typeName ?? "")) return m.value;
  const out: Record<string, any> = {};
  for (const [key, v] of Object.entries(m)) {
    if (key.startsWith("$")) continue;
    const name = key.replace(/[A-Z]/g, (c) => "_" + c.toLowerCase());
    out[name] = ["config", "metadata", "staticOptions", "engines", "maxDdPercentiles"].includes(key)
      ? v
      : plain(v);
  }
  return out;
}
export const json = (value: Record<string, any> = {}): JsonObject => value;
export const provider = (name: string, config: Record<string, any> = {}) => ({
  provider: name,
  config: json(config),
});
export function account(value: app.Account): ui.Account {
  return {
    ...plain(value),
    provider: value.provider?.provider ?? "",
    config: value.provider?.config ?? {},
    status: app.AccountStatus[value.status].toLowerCase(),
  };
}
export function backtest(value: app.Backtest): ui.Backtest {
  return {
    ...plain(value),
    market_provider: value.market?.provider ?? "",
    market_config: value.market?.config ?? {},
    strategy_provider: value.strategy?.provider ?? "",
    strategy_config: value.strategy?.config ?? {},
    engine_provider: value.engine?.provider ?? "",
    engine_config: value.engine?.config ?? {},
    status: app.BacktestStatus[value.status].toLowerCase(),
    result: value.result && result(value.result),
  };
}
export function order(value: app.OrderRecord): ui.OrderRecord {
  return {
    ...plain(value),
    side: app.OrderSide[value.side],
    position_side: value.positionSide ? app.PositionSide[value.positionSide] : "",
    order_type: app.OrderType[value.orderType],
  };
}
export function position(value: app.Position): ui.Position {
  return { ...plain(value), side: app.PositionSide[value.side] };
}
export function result(value: RpcResult): ui.BacktestResult {
  const stats = value.stats ?? value.preview?.stats;
  const trades = stats?.trades;
  return {
    ...plain(value),
    default_qty: value.defaultQty,
    equity_curve: value.equityCurve,
    compounding: false,
    outputs: value.outputs
      ? {
          ...plain(value.outputs),
          plots: value.outputs.plots.map((p) => ({
            title: p.title,
            value: p.value ?? null,
          })),
        }
      : undefined,
    ...(stats && {
      net_profit: stats.netProfit,
      profit_pct_fiat: stats.profitPct,
      profit_factor: stats.profitFactor,
      max_drawdown: stats.maxDrawdown,
      max_drawdown_pct: stats.maxDrawdownPct,
      sharpe_ratio: stats.sharpeRatio,
      sortino_ratio: stats.sortinoRatio,
    }),
    ...(trades && {
      total_trades: Number(trades.total),
      winning_trades: Number(trades.winning),
      losing_trades: Number(trades.losing),
      win_rate: trades.winRate,
      avg_win_trade: trades.avgWin,
      avg_loss_trade: trades.avgLoss,
      max_consec_wins: Number(trades.maxConsecutiveWins),
      max_consec_losses: Number(trades.maxConsecutiveLosses),
      avg_trade_duration_s: trades.avgDurationS,
    }),
  };
}
export const category = {
  account: cfg.ConfigurableCategory.ACCOUNT_PROVIDER,
  market: cfg.ConfigurableCategory.MARKET_PROVIDER,
  strategy: cfg.ConfigurableCategory.STRATEGY,
  engine: cfg.ConfigurableCategory.ENGINE,
  notification_channel: cfg.ConfigurableCategory.NOTIFICATION_CHANNEL,
};
export function categoryOf(value: string) {
  if (!(value in category)) throw new Error(`Unknown configurable category: ${value}`);
  return category[value as keyof typeof category];
}
export function categoryName(value: cfg.ConfigurableCategory) {
  return Object.entries(category).find(([, v]) => v === value)?.[0] ?? "";
}
export function params(value: cfg.ParamsResponse): ui.ParamsResponse {
  return {
    ...plain(value),
    params: value.params.map((p) => ({
      ...plain(p),
      type: cfg.ParamType[p.type].toLowerCase(),
      category: categoryName(p.category),
      default: p.defaultValue ? toJson(ValueSchema, p.defaultValue) : null,
    })),
  };
}
export function strategyInfo(value: strategy.StrategyInfoResponse): StrategyInfoResponse {
  const info = value.info;
  return {
    ...plain(value),
    status: strategy.StrategyInfoStatus[value.status].toLowerCase(),
    info: info && {
      ...plain(info),
      kind: strategy.ScriptKind[info.kind].toLowerCase(),
      backtest: info.backtest && {
        ...plain(info.backtest),
        order_size: info.backtest.orderSize && {
          ...plain(info.backtest.orderSize),
          unit: strategy.OrderSizeUnit[info.backtest.orderSize.unit].toLowerCase(),
        },
        commission: info.backtest.commission && {
          ...plain(info.backtest.commission),
          unit: strategy.CommissionUnit[info.backtest.commission.unit].toLowerCase(),
        },
      },
    },
    engines: Object.fromEntries(Object.entries(value.engines).map(([key, v]) => [key, plain(v)])),
  };
}
export function providerInfos(...responses: cfg.ProviderInfosResponse[]) {
  const out = {
    account_providers: [],
    market_providers: [],
    strategies: [],
    engines: [],
    notification_channels: [],
  } as Record<string, ui.ProviderInfoWire[]>;
  const keys = [
    "",
    "account_providers",
    "market_providers",
    "strategies",
    "engines",
    "notification_channels",
  ];
  for (const res of responses)
    for (const group of res.providers)
      if (keys[group.category]) out[keys[group.category]!] = group.providers.map((p) => plain(p));
  return out as {
    account_providers: ui.ProviderInfoWire[];
    market_providers: ui.ProviderInfoWire[];
    strategies: ui.ProviderInfoWire[];
    engines: ui.ProviderInfoWire[];
    notification_channels: ui.ProviderInfoWire[];
  };
}
